import { Controller } from "@hotwired/stimulus"
import { Chart, registerables } from "chart.js"
import annotationPlugin from 'chartjs-plugin-annotation'

Chart.register(...registerables, annotationPlugin)

export default class extends Controller {
  static targets = ["propertiesUsersChart", "filesSizeChart", "lineChart", "billingChart"]
  static values = { chartsDataUrl: String }

  connect() {
    this.loadChartData()
  }

  async loadChartData() {
    try {
      console.log('Loading chart data from:', this.chartsDataUrlValue)
      const response = await fetch(this.chartsDataUrlValue)
      const data = await response.json()
      console.log('Chart data loaded:', data)
      
      // Use different labels for each chart
      this.createPropertiesUsersChart(data.stacked_data, data.properties_users_labels || data.stacked_labels)
      this.createFilesSizeChart(data.stacked_data, data.stacked_labels)
      
      // Only create line chart if line data exists and has actual data
      if (data.line_data && this.hasLineChartTarget && this.hasLineData(data.line_data)) {
        this.createLineChart(data.line_data, data.line_labels)
      }
      
      if (data.billing_data) {
        this.createBillingChart(data.billing_data)
      }
    } catch (error) {
      console.error('Error loading chart data:', error)
    }
  }

  createPropertiesUsersChart(stackedData, stackedLabels) {
    const ctx = this.propertiesUsersChartTarget.getContext('2d')
    const tenants = Object.keys(stackedData)

    // Prepare data - handle both single tenant and multi-tenant data
    const propertiesData = stackedLabels.map(date => {
      if (tenants.length === 1) {
        // Single tenant data
        return stackedData[tenants[0]][date]?.properties || 0
      } else {
        // Multi-tenant data (dashboard)
        return tenants.reduce((sum, tenant) => {
          return sum + (stackedData[tenant][date]?.properties || 0)
        }, 0)
      }
    })

    const buildingsData = stackedLabels.map(date => {
      if (tenants.length === 1) {
        // Single tenant data
        return stackedData[tenants[0]][date]?.buildings || 0
      } else {
        // Multi-tenant data (dashboard)
        return tenants.reduce((sum, tenant) => {
          return sum + (stackedData[tenant][date]?.buildings || 0)
        }, 0)
      }
    })

    new Chart(ctx, {
      type: 'bar',
      data: {
        labels: stackedLabels,
        datasets: [
          {
            label: 'Ingatlanok száma',
            type: 'bar',
            data: propertiesData,
            backgroundColor: 'rgba(54, 162, 235, 0.8)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1,
            yAxisID: 'y'
          },
          {
            label: 'Épületek száma',
            type: 'line',
            data: buildingsData,
            borderColor: 'rgba(153, 102, 255, 1)',
            backgroundColor: 'rgba(153, 102, 255, 0.1)',
            borderWidth: 3,
            fill: false,
            tension: 0.4,
            yAxisID: 'y1'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            mode: 'index',
            intersect: false
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Hét'
            }
          },
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            beginAtZero: true,
            title: {
              display: true,
              text: 'Ingatlanok száma'
            }
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            beginAtZero: true,
            title: {
              display: true,
              text: 'Épületek száma'
            },
            grid: {
              drawOnChartArea: false,
            }
          }
        }
      }
    })
  }

  createFilesSizeChart(stackedData, stackedLabels) {
    const ctx = this.filesSizeChartTarget.getContext('2d')
    const tenants = Object.keys(stackedData)
    
    // Find current week to split historical and forecast data
    const today = new Date()
    const currentWeekStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - today.getDay())
    const currentWeekEnd = new Date(currentWeekStart.getTime() + 6 * 24 * 60 * 60 * 1000)
    const currentWeekKey = currentWeekStart.toISOString().split('T')[0].replace(/-/g, '.') + ' - ' + 
                          currentWeekEnd.toISOString().split('T')[0].split('-').slice(1).join('.')
    
    // Find the index of current week in labels
    const todayIndex = stackedLabels.findIndex(label => {
      const weekStart = label.split(' - ')[0]
      return weekStart === currentWeekStart.toISOString().split('T')[0].replace(/-/g, '.')
    })
    
    // If current week not found, use the last historical week
    const finalTodayIndex = todayIndex >= 0 ? todayIndex : Math.floor(stackedLabels.length * 0.7)
    
    // Prepare data - handle both single tenant and multi-tenant data
    const filesData = stackedLabels.map(date => {
      if (tenants.length === 1) {
        // Single tenant data
        return stackedData[tenants[0]][date]?.files || 0
      } else {
        // Multi-tenant data (dashboard)
        return tenants.reduce((sum, tenant) => {
          return sum + (stackedData[tenant][date]?.files || 0)
        }, 0)
      }
    })

    const filesSizeData = stackedLabels.map(date => {
      if (tenants.length === 1) {
        // Single tenant data
        return stackedData[tenants[0]][date]?.filesSize || 0
      } else {
        // Multi-tenant data (dashboard)
        return tenants.reduce((sum, tenant) => {
          return sum + (stackedData[tenant][date]?.filesSize || 0)
        }, 0)
      }
    })

    // Create combined datasets - historical data for all weeks, forecast only for future weeks
    const combinedFilesData = filesData.map((value, index) => {
      return index <= finalTodayIndex ? value : null
    })
    const forecastFilesData = filesData.map((value, index) => {
      return index > finalTodayIndex ? value : null
    })
    
    const combinedFilesSizeData = filesSizeData.map((value, index) => {
      return index <= finalTodayIndex ? value : null
    })
    const forecastFilesSizeData = filesSizeData.map((value, index) => {
      return index > finalTodayIndex ? value : null
    })

    new Chart(ctx, {
      type: 'bar',
      data: {
        labels: stackedLabels,
        datasets: [
          {
            label: 'Fájlok száma',
            type: 'bar',
            data: combinedFilesData.map((value, index) => 
              value !== null ? value : (forecastFilesData[index] || null)
            ),
            backgroundColor: function(context) {
              // Use different colors for historical vs forecast data
              const index = context.dataIndex;
              return index <= finalTodayIndex ? 
                'rgba(75, 192, 192, 0.8)' : 
                'rgba(75, 192, 192, 0.3)';
            },
            borderColor: 'rgba(75, 192, 192, 1)',
            borderWidth: function(context) {
              const index = context.dataIndex;
              return index <= finalTodayIndex ? 1 : 2;
            },
            borderDash: function(context) {
              const index = context.dataIndex;
              return index <= finalTodayIndex ? [] : [8, 4];
            },
            yAxisID: 'y',
            barPercentage: 0.9,
            categoryPercentage: 0.8
          },
          {
            label: 'Fájlok össz méret (GB)',
            type: 'line',
            data: combinedFilesSizeData,
            borderColor: 'rgba(255, 205, 86, 1)',
            backgroundColor: 'rgba(255, 205, 86, 0.1)',
            borderWidth: 3,
            fill: false,
            tension: 0.4,
            yAxisID: 'y1'
          },
          {
            label: 'Fájlok össz méret (GB) - Előrejelzés',
            type: 'line',
            data: forecastFilesSizeData,
            borderColor: 'rgba(255, 205, 86, 1)',
            backgroundColor: 'rgba(255, 205, 86, 0.1)',
            borderWidth: 3,
            borderDash: [12, 6],
            fill: false,
            tension: 0.4,
            yAxisID: 'y1'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context) {
                const value = context.parsed.y
                if (value === null || value === undefined) {
                  return null
                }
                
                let label = context.dataset.label
                if (context.dataset.label.includes('Előrejelzés') || context.dataset.borderDash) {
                  label += ' (prognózis)'
                }
                
                if (context.dataset.label.includes('Méret') || context.dataset.yAxisID === 'y1') {
                  const gb = value / (1024 * 1024 * 1024)
                  return label + ': ' + gb.toFixed(2) + ' GB'
                }
                return label + ': ' + value.toLocaleString()
              }
            }
          },
          annotation: {
            annotations: {
              line1: {
                type: 'line',
                xMin: finalTodayIndex + 0.5, // Position between the last historical and first forecast week
                xMax: finalTodayIndex + 0.5,
                borderColor: 'rgb(255, 99, 132)',
                borderWidth: 2,
                borderDash: [5, 5],
                label: {
                  content: 'Múlt ↔ Jövő',
                  enabled: true,
                  position: 'start'
                }
              }
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Hét'
            },
            offset: true,
            grid: {
              offset: true
            }
          },
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            beginAtZero: true,
            title: {
              display: true,
              text: 'Fájlok száma'
            }
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            beginAtZero: true,
            title: {
              display: true,
              text: 'Fájl méret (GB)'
            },
            grid: {
              drawOnChartArea: false,
            },
            ticks: {
              callback: function(value) {
                return (value / (1024 * 1024 * 1024)).toFixed(2)
              }
            }
          }
        }
      }
    })
  }

  createLineChart(lineData, lineLabels) {
    const ctx = this.lineChartTarget.getContext('2d')
    console.log('Creating line chart with data:', lineData, 'and labels:', lineLabels)
    
    // Prepare data for line chart (synchronized houses)
    const synchronizedData = lineLabels.map(date => {
      return Object.values(lineData).reduce((sum, tenantData) => {
        // Handle undefined or null values safely
        if (!tenantData || !tenantData[date]) return sum
        return sum + (tenantData[date].ehaz_synchronized_houses || 0)
      }, 0)
    })

    // Prepare data for webhook calls
    const webhookData = lineLabels.map(date => {
      return Object.values(lineData).reduce((sum, tenantData) => {
        // Handle undefined or null values safely
        if (!tenantData || !tenantData[date]) return sum
        return sum + (tenantData[date].webhook_calls || 0)
      }, 0)
    })
    
    console.log('Synchronized data:', synchronizedData)
    console.log('Webhook data:', webhookData)

    new Chart(ctx, {
      type: 'line',
      data: {
        labels: lineLabels,
        datasets: [{
          label: 'Szinkronizált házak száma (rendszer szinten)',
          data: synchronizedData,
          borderColor: 'rgba(220, 53, 69, 1)',
          backgroundColor: 'rgba(220, 53, 69, 0.1)',
          borderWidth: 3,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: 'rgba(220, 53, 69, 1)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 6,
          pointHoverRadius: 8,
          yAxisID: 'y'
        }, {
          label: 'Webhook hívások száma',
          type: 'bar',
          data: webhookData,
          backgroundColor: 'rgba(13, 202, 240, 0.8)', // Bootstrap info color
          borderColor: 'rgba(13, 202, 240, 1)',
          borderWidth: 1,
          yAxisID: 'y1'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          title: {
            display: true,
            text: 'eHÁZ szinkronizált házak trend',
            font: {
              size: 16
            }
          },
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context) {
                const value = context.parsed.y
                if (value === null || value === undefined) {
                  return null
                }
                return context.dataset.label + ': ' + value.toLocaleString()
              }
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Hét'
            }
          },
          y: {
            type: 'linear',
            display: true,
            position: 'left',
            beginAtZero: true,
            title: {
              display: true,
              text: 'Szinkronizált házak száma'
            },
            ticks: {
              precision: 0
            }
          },
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            beginAtZero: true,
            title: {
              display: true,
              text: 'Webhook hívások száma'
            },
            ticks: {
              precision: 0
            },
            grid: {
              drawOnChartArea: false,
            }
          }
        },
        interaction: {
          mode: 'index',
          intersect: false
        }
      }
    })
  }

  getMetricLabel(metric) {
    const labels = {
      properties: 'Ingatlanok',
      files: 'Fájlok száma', 
      filesSize: 'Fájlok össz méret',
      users: 'Felhasználók száma'
    }
    return labels[metric] || metric
  }

  hasLineData(lineData) {
    // Always show the line chart, even if there's no data (will show zeros)
    // Just check if the structure exists
    const tenants = Object.keys(lineData)
    return tenants.length > 0 && tenants.some(tenant => {
      const tenantData = lineData[tenant]
      return Object.keys(tenantData).length > 0
    })
  }

  createBillingChart(billingData) {
    const ctx = this.billingChartTarget.getContext('2d')

    // Convert labels to month names and find year boundaries
    const monthNames = [
      'Jan', 'Feb', 'Már', 'Ápr', 'Máj', 'Jún',
      'Júl', 'Aug', 'Szept', 'Okt', 'Nov', 'Dec'
    ]

    const displayLabels = billingData.labels.map(label => {
      const [year, month] = label.split('.')
      return `${monthNames[parseInt(month) - 1]} ${year}`
    })

    // Find year boundaries for vertical lines
    const yearBoundaries = []
    let currentYear = null
    billingData.labels.forEach((label, index) => {
      const [year] = label.split('.')
      if (currentYear !== year) {
        if (currentYear !== null) {
          yearBoundaries.push(index)
        }
        currentYear = year
      }
    })

    // Calculate unpaid invoices (negative values for display below zero line)
    const unpaidInvoices = billingData.data.map(item => {
      const unpaid = item.all_invoices - item.paid_invoices
      return unpaid > 0 ? -unpaid : 0 // Make negative for display below zero
    })

    new Chart(ctx, {
      type: 'bar',
      data: {
        labels: displayLabels,
        datasets: [{
          label: 'Összes kiállított számla (HUF)',
          type: 'bar',
          data: billingData.data.map(item => item.all_invoices),
          backgroundColor: 'rgba(13, 110, 253, 0.8)', // Bootstrap primary blue
          borderColor: 'rgba(13, 110, 253, 1)',
          borderWidth: 1,
          yAxisID: 'y'
        }, {
          label: 'Befizetett számlák (HUF)',
          type: 'line',
          data: billingData.data.map(item => item.paid_invoices),
          borderColor: 'rgba(40, 167, 69, 1)', // Bootstrap success green
          backgroundColor: 'rgba(40, 167, 69, 0.1)',
          borderWidth: 3,
          fill: false,
          tension: 0.3,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: 'rgba(40, 167, 69, 1)',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          yAxisID: 'y'
        }, {
          label: 'Ki nem fizetett számlák (HUF)',
          type: 'line',
          data: unpaidInvoices, // Already negative values
          borderColor: 'rgba(220, 53, 69, 1)', // Bootstrap danger red
          backgroundColor: 'rgba(220, 53, 69, 0.1)',
          borderWidth: 3,
          fill: false,
          tension: 0.3,
          pointRadius: 4,
          pointHoverRadius: 6,
          pointBackgroundColor: 'rgba(220, 53, 69, 1)',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          yAxisID: 'y'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            stacked: false, // Grouped bars (not stacked)
            title: {
              display: true,
              text: 'Hónap'
            },
            ticks: {
              maxRotation: 45,
              minRotation: 45
            }
          },
          y: {
            beginAtZero: false, // Allow negative values
            title: {
              display: true,
              text: 'Összeg (HUF)'
            },
            ticks: {
              callback: function(value) {
                return new Intl.NumberFormat('hu-HU', {
                  style: 'currency',
                  currency: 'HUF',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(value) // Show actual value (can be negative)
              }
            }
          }
        },
        plugins: {
          title: {
            display: true,
            text: 'Havi kiszámlázott összegek trendje',
            font: {
              size: 16
            }
          },
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            mode: 'index',
            intersect: false,
            callbacks: {
              label: function(context) {
                const value = context.parsed.y
                let label = context.dataset.label
                
                return label + ': ' + new Intl.NumberFormat('hu-HU', {
                  style: 'currency',
                  currency: 'HUF',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(value) // Show actual value (can be negative)
              }
            }
          },
          annotation: {
            annotations: yearBoundaries.reduce((annotations, boundary, index) => {
              annotations[`yearLine${index}`] = {
                type: 'line',
                xMin: boundary - 0.5,
                xMax: boundary - 0.5,
                borderColor: 'rgba(255, 99, 132, 0.8)',
                borderWidth: 2,
                borderDash: [5, 5],
                label: {
                  content: 'Év váltás',
                  enabled: true,
                  position: 'start'
                }
              }
              return annotations
            }, {})
          }
        }
      }
    })
  }

}

// Helper function to format file size
function formatFileSize(bytes) {
  if (bytes === 0) return '0 B'
  
  // Always show in GB for chart tooltips
  const gb = bytes / (1024 * 1024 * 1024)
  return gb.toFixed(2) + ' GB'
}
